import { Badge, Timestamp, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { MetaConversation, Tag } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import type { Conversation } from 'lib-shared-types-api';
import { memo } from 'react';

import { ConversationPreviewTags } from 'app/views/layout/views/search/components/results/components/types/conversations/components/preview/components/header/components/tags/conversationPreviewTags';
import { ConversationStatusColorBox } from 'components/conversation';
import { NewMessageIndicator } from 'components/conversation/conversationDetails/components/NewMessageIndicator';
import { RequesterAvatar } from 'components/conversation/conversationDetails/components/RequesterAvatar';
import { TypingIndicator } from 'components/conversation/conversationDetails/components/TypingIndicator';
import { subTitleRenderers } from 'components/conversation/conversationDetails/subTitleRenderers';
import { useBreakpointValue } from 'utils/mediaQuery';

import { DirectionIcon } from '../../../assets/components/conversation/components/conversationStatusIcon/components/DirectionIcon';
import { ConversationChannelIcon } from '../conversationChannelIcon';

import { Title } from './components/Title';
import styles from './conversationDetails.module.scss';
import { ConversationDetailsLayout } from './conversationDetailsLayout';

export type ConversationDetailsProps = {
  /** Extra classname(s) */
  className?: string;
  /** Conversation URL */
  conversationUrl?: string;
  /** Name of the conversation */
  conversationName: string | null;
  /** Main conversation details object */
  metaConversation: MetaConversation;
  /** Layout */
  layout?: ConversationDetailsLayout;
  /** Is user typing */
  isTyping?: boolean;
  /** New message count */
  newMessageCount?: number;
  /** Whether the title links to the conversation */
  linkToConversation?: boolean;
  /** Whether we want to show the message count */
  showMessageCount?: boolean;
  /** Whether we want to show tags */
  showTags?: boolean;
  /** Tags */
  tags?: Array<Required<Tag>>;
  /** Controls whether the requester avatar is shown or not */
  showRequesterAvatar?: boolean;
  /** Used to customize the contents of the title */
  titleRenderer?(props: ConversationDetailsProps): React.ReactNode;
  /** Used to customize the contents of the subtitle */
  subTitleRenderer?(props: ConversationDetailsProps): React.ReactNode;
  /** Triggered when click is within of the subTitle element */
  onSubTitleClick?: React.MouseEventHandler<HTMLElement>;
  /** Triggered when click is within of the title element */
  onTitleClick?: React.MouseEventHandler<HTMLElement>;
};

export const ConversationDetails = memo(function ConversationDetails(
  props: ConversationDetailsProps,
): JSX.Element {
  const {
    layout = ConversationDetailsLayout.Overview,
    isTyping,
    newMessageCount = 0,
    linkToConversation = true,
    showTags,
    showRequesterAvatar = true,
    conversationUrl,
    className,
    metaConversation,
    conversationName,
    tags,
    titleRenderer,
    subTitleRenderer = subTitleRenderers[layout],
    onSubTitleClick,
    onTitleClick,
  } = props;

  const isSmallScreen = useBreakpointValue({ base: true, sm: false });

  const adjustedLayout =
    layout === ConversationDetailsLayout.SidebarCollapsed && isSmallScreen
      ? ConversationDetailsLayout.Sidebar
      : layout;

  const { conversation, is_updating: isUpdating } = metaConversation;

  const {
    status,
    initial_channel: channel,
    direction,
    pending_expiration_at: pendingAt,
  } = conversation;

  const hasFollowUp = status === 'pending' || status === 'awaitingpending';
  const isSidebarConversation =
    adjustedLayout === ConversationDetailsLayout.Sidebar ||
    adjustedLayout === ConversationDetailsLayout.SidebarCollapsed;

  const rootClass = classnames(
    {
      [styles.details]: true,
      [styles.detailsActive]: status === 'open',
      [styles.detailsFollowup]: hasFollowUp,
      [styles.detailsUpdating]: isUpdating,
      [styles.detailsSidebar]:
        adjustedLayout === ConversationDetailsLayout.Sidebar,
      [styles.detailsSidebarCollapsed]:
        adjustedLayout === ConversationDetailsLayout.SidebarCollapsed,
    },
    className,
  );

  const getTags = () => {
    if (tags?.length) {
      return <ConversationPreviewTags tags={tags} scrollable />;
    }
    return null;
  };

  return (
    <div className={rootClass}>
      <ConversationStatusColorBox
        csid={conversation.id as Conversation['id']}
        status={status as Conversation['status']}
        margin="0 100 0 0"
        height="32px"
        borderRadius="100"
        alignSelf="center"
        flex="0 0 4px"
      />
      {metaConversation.requester && showRequesterAvatar && (
        <Tooltip content={conversationName}>
          <div className={styles.requesterAvatar}>
            <TypingIndicator isTyping={isTyping ?? false} />
            <NewMessageIndicator
              layout={adjustedLayout}
              newMessageCount={newMessageCount}
            />

            <RequesterAvatar
              layout={adjustedLayout}
              metaConversation={metaConversation}
            />
          </div>
        </Tooltip>
      )}
      <div className={styles.textWrap}>
        <h3 className={styles.title}>
          {channel && (
            <ConversationChannelIcon
              className={styles.channelIcon}
              channel={channel}
            />
          )}

          {direction && (
            <DirectionIcon
              direction={direction}
              className={styles.directionIcon}
            />
          )}

          <div className={styles.titleContent} onClick={onTitleClick}>
            {titleRenderer ? (
              titleRenderer(props)
            ) : (
              <Title
                layout={adjustedLayout}
                metaConversation={metaConversation}
                conversationUrl={conversationUrl}
                linkToConversation={linkToConversation}
              />
            )}
          </div>

          {!isSidebarConversation && hasFollowUp && !!pendingAt && (
            <Badge variant="info" size="small" className={styles.badge}>
              Follow up expires{' '}
              <span
                onClick={() =>
                  ampli.conversationsTimestampClicked({
                    context: 'followup',
                  })
                }
              >
                <Timestamp relative time={pendingAt} />
              </span>
            </Badge>
          )}

          {isUpdating && (
            <Badge variant="secondary" size="small" className={styles.badge}>
              Updating...
            </Badge>
          )}
        </h3>

        <p className={styles.subTitle} onClick={onSubTitleClick}>
          {subTitleRenderer(props)}
        </p>

        {showTags && <span>{getTags()}</span>}
      </div>
    </div>
  );
});
